<template>
  <div class="salesman-home">
    <header class="home-header">
      <div class="header-row">
        <div class="header-text">
          <p class="home-label">三高修复·业务员</p>
          <h1>{{ salesmanName || "业务员" }}</h1>
          <p v-if="orgInfo" class="home-org">{{ orgInfo.orgName }}</p>
        </div>
        <button class="logout-button-sm" @click="logout">退出</button>
      </div>
      <div class="header-meta">
        <span>业务员编码 {{ salesmanCode }}</span>
        <span class="dot">·</span>
        <span>手机号 {{ maskedPhone }}</span>
      </div>
    </header>

    <section class="link-card" @click="goQrcode">
      <div class="link-icon">
        <van-icon name="share-o" />
      </div>
      <div class="link-text">
        <strong>生成问卷链接</strong>
        <small>分享给客户填写问卷，提交后自动归属到您的名下</small>
      </div>
      <van-icon name="arrow" color="#9bb1c4" />
    </section>

    <!-- 暂时隐藏关注公众号入口，保留模板及逻辑，后续恢复时取消注释即可。
    <section v-if="!taikangMode" class="link-card" @click="goOfficialAccount">
      <div class="link-icon oa">
        <van-icon name="wechat" />
      </div>
      <div class="link-text">
        <strong>关注公众号</strong>
        <small>关注「元脉健康」，后续可从公众号底部菜单进入工作台</small>
      </div>
      <van-icon name="arrow" color="#9bb1c4" />
    </section>
    -->

    <section ref="customerSection" class="customer-section">
      <div class="section-title">
        <span>我的客户</span>
        <div class="customer-title-actions">
          <small>共 {{ customerDataReady ? totalCount : '—' }} 位</small>
          <button type="button" :disabled="loading || paying || refunding" @click="refreshExamSummary">体检进度</button>
        </div>
      </div>

      <van-dropdown-menu class="filter-dropdown" active-color="#1677c8" :close-on-click-outside="true">
        <van-dropdown-item ref="auditDropdown" :title="auditFilterTitle">
          <div class="filter-panel-heading">客户状态<span>选择要查看的客户</span></div>
          <button
            v-for="option in auditOptions"
            :key="String(option.value)"
            type="button"
            class="filter-option"
            :class="{ 'is-selected': activeFilter === option.value }"
            :aria-pressed="activeFilter === option.value"
            @click="selectCustomerFilter('audit', option.value)"
          >
            <span class="filter-option-label">{{ option.label }}</span>
            <span class="filter-option-count">{{ option.count }}</span>
            <van-icon class="filter-option-check" :name="activeFilter === option.value ? 'checked' : 'circle'" />
          </button>
        </van-dropdown-item>
        <van-dropdown-item
          v-if="showExamFilter"
          ref="examDropdown"
          :title="examFilterTitle"
        >
          <div class="filter-panel-heading">体检进度<span>按最新同步状态筛选</span></div>
          <button
            v-for="option in examOptions"
            :key="String(option.value)"
            type="button"
            class="filter-option"
            :class="{ 'is-selected': examFilter === option.value }"
            :aria-pressed="examFilter === option.value"
            @click="selectCustomerFilter('exam', option.value)"
          >
            <span class="filter-option-label">{{ option.label }}</span>
            <span class="filter-option-count">{{ option.count }}</span>
            <van-icon class="filter-option-check" :name="examFilter === option.value ? 'checked' : 'circle'" />
          </button>
        </van-dropdown-item>
      </van-dropdown-menu>

      <div class="customer-list">
        <div v-if="loading" class="state-cell">
          <van-loading color="#168de0" size="22" vertical>加载中...</van-loading>
        </div>
        <div v-else-if="customerLoadError" class="state-cell" role="alert">
          <p>{{ customerLoadError }}</p>
          <button type="button" class="pay-single-button ghost" @click="loadCurrent">重新加载</button>
        </div>
        <div v-else-if="!filteredCustomers.length" class="state-cell empty">
          <van-icon name="user-o" size="32" color="#b4cad9" />
          <p>{{ activeFilter !== null || examFilter !== null ? '暂无符合当前筛选条件的客户' : '暂无客户' }}</p>
        </div>
        <article
          v-for="item in filteredCustomers"
          :key="item.id"
          class="customer-card"
        >
          <div class="customer-top">
            <span class="customer-name">
              {{ item.nickname || "匿名用户" }}
            </span>
            <span class="audit-badge" :class="`audit-${item.auditStatus}`">
              {{ auditText(item.auditStatus) }}
            </span>
          </div>
          <div class="customer-tags-row">
            <div v-if="item.tags.length" class="customer-tags" aria-label="客户标签">
              <span v-for="tag in item.tags" :key="tag" class="customer-tag">{{ tag }}</span>
            </div>
            <button
              type="button"
              class="edit-tags-button"
              :disabled="paying || refunding || savingTags"
              :aria-label="`${item.nickname || '匿名用户'}：${item.tags.length ? '编辑标签' : '添加标签'}`"
              @click.stop="openTagEditor(item)"
            >
              <van-icon :name="item.tags.length ? 'edit' : 'plus'" />
              {{ item.tags.length ? '编辑标签' : '添加标签' }}
            </button>
          </div>
          <div class="customer-meta">
            <span>手机号：{{ item.phone || "-" }}</span>
          </div>
          <div class="customer-meta">
            <span>身份证：{{ maskIdCard(item.idCard) }}</span>
          </div>
          <div v-if="item.auditStatus === 3" class="customer-meta">
            <span>
              体检：
              <span class="exam-inline" :class="`exam-${examStatusSafe(item)}`">
                {{ examText(item) }}
              </span>
              <template v-if="item.reservationDate">
                （{{ formatDate(item.reservationDate) }}
                <template v-if="item.reservationTime"> {{ item.reservationTime }}</template>）
              </template>
            </span>
          </div>
          <div class="customer-footer">
            <span>提交时间：{{ formatTime(item.createTime) }}</span>
            <span v-if="item.auditStatus === 1" class="footer-buttons">
              <button
                type="button"
                class="pay-single-button"
                :disabled="paying"
                @click.stop="confirmPay(item)"
              >
                支付
              </button>
            </span>
            <span v-else-if="item.auditStatus === 3" class="footer-buttons">
              <button
                type="button"
                class="pay-single-button ghost"
                @click.stop="goExam(item)"
              >
                体检
              </button>
              <button
                v-if="item.refundable"
                type="button"
                class="pay-single-button refund"
                :disabled="refunding"
                @click.stop="confirmRefund(item)"
              >
                退款
              </button>
            </span>
          </div>
        </article>
      </div>
    </section>

    <van-popup
      v-model="showTagEditor"
      position="bottom"
      round
      class="tag-editor"
      :close-on-click-overlay="false"
    >
      <div class="tag-editor-heading">
        <div>
          <h2>客户标签</h2>
          <p>{{ tagTarget ? (tagTarget.nickname || '匿名用户') : '' }}</p>
        </div>
        <button type="button" class="tag-editor-close" :disabled="savingTags" aria-label="关闭标签编辑" @click="closeTagEditor">
          <van-icon name="cross" />
        </button>
      </div>
      <div class="tag-editor-body">
        <p class="tag-editor-hint">最多 {{ maxCustomerTags }} 个标签，每个不超过 {{ maxCustomerTagLength }} 字</p>
        <div class="tag-input-row">
          <van-field
            v-model="tagInput"
            class="tag-input"
            label="自定义标签"
            :border="false"
            :maxlength="maxCustomerTagLength * 2"
            :disabled="savingTags"
            placeholder="如：重点跟进、周末联系"
            @input="tagError = ''"
            @keyup.enter="addCustomerTag"
          />
          <button type="button" class="tag-add-button" :disabled="savingTags" @click="addCustomerTag">添加</button>
        </div>
        <p v-if="tagError" class="tag-editor-error" role="alert">{{ tagError }}</p>
        <div class="tag-draft-heading">已添加 <span>{{ tagDraft.length }}/{{ maxCustomerTags }}</span></div>
        <div v-if="tagDraft.length" class="tag-draft-list">
          <span v-for="(tag, index) in tagDraft" :key="tag" class="customer-tag tag-draft-item">
            <span>{{ tag }}</span>
            <button type="button" :disabled="savingTags" :aria-label="`删除标签：${tag}`" @click="removeCustomerTag(index)">
              <van-icon name="cross" />
            </button>
          </span>
        </div>
        <p v-else class="tag-empty">还没有标签，可添加便于跟进的客户标记</p>
      </div>
      <div class="tag-editor-actions">
        <van-button :disabled="savingTags" @click="closeTagEditor">取消</van-button>
        <van-button type="info" :loading="savingTags" loading-text="保存中" @click="saveCustomerTags">保存标签</van-button>
      </div>
    </van-popup>

    <van-dialog
      v-model="showExamSummary"
      title="客户体检进度"
      confirm-button-text="我知道了"
      :close-on-click-overlay="false"
      @closed="scrollToExamResults"
    >
      <div class="exam-summary-body">
        <p class="exam-summary-intro"></p>
        <div class="exam-summary-stats">
          <button type="button" class="exam-summary-stat" @click="viewExamCustomers(3)">
            <span>已到检</span>
            <strong>{{ countByExamStatus(3) }}<small>人</small></strong>
            <em>待出报告</em>
            <span class="exam-summary-link">查看客户 <van-icon name="arrow" /></span>
          </button>
          <button type="button" class="exam-summary-stat reported" @click="viewExamCustomers(4)">
            <span>已出报告</span>
            <strong>{{ countByExamStatus(4) }}<small>人</small></strong>
            <em>报告已出具</em>
            <span class="exam-summary-link">查看客户 <van-icon name="arrow" /></span>
          </button>
          <button type="button" class="exam-summary-stat reported" @click="viewExamCustomers(5)">
            <span>已传报告</span>
            <strong>{{ countByExamStatus(5) }}<small>人</small></strong>
            <em>报告已上传</em>
            <span class="exam-summary-link">查看客户 <van-icon name="arrow" /></span>
          </button>
        </div>
        <p class="exam-summary-note">统计您名下已支付客户，按当前体检状态分类，以系统最近记录为准。</p>
      </div>
    </van-dialog>

    <van-dialog
      v-model="showPayConfirm"
      show-cancel-button
      @confirm="startPay"
    >
      <template #title>
        <span class="pay-confirm-title">
          <span>支付体检费</span>
          <a
            v-if="showReimbursementInfo"
            class="pay-subsidy-link"
            :href="subsidyPdfUrl"
            aria-label="查看体检费用补贴说明"
            @click.prevent="showSubsidyPreview = true"
          >说明</a>
        </span>
      </template>
      <div class="pay-confirm-body pay-confirm-message">
        确认为客户「<strong class="pay-confirm-name">{{ payTargetName }}</strong>」<span class="pay-confirm-action">支付体检费用？</span>
      </div>
    </van-dialog>

    <van-popup
      v-if="showReimbursementInfo"
      v-model="showSubsidyPreview"
      position="bottom"
      class="subsidy-preview"
      :z-index="3000"
      :close-on-click-overlay="false"
    >
      <div class="subsidy-preview-header">
        <span>体检费用补贴说明</span>
        <button type="button" @click="showSubsidyPreview = false">关闭</button>
      </div>
      <div v-if="showSubsidyPreview" class="subsidy-preview-content">
        <div v-if="subsidyPdfError" class="subsidy-preview-error">
          文档加载失败，<a :href="subsidyPdfUrl" target="_blank" rel="noopener noreferrer">打开原文件</a>
        </div>
        <template v-else>
          <van-loading v-if="subsidyPdfLoading" size="20" vertical>加载中...</van-loading>
          <pdf
            :src="subsidyPdfUrl"
            :page="1"
            @num-pages="subsidyPdfPages = $event || 0"
            @page-loaded="subsidyPdfLoading = false"
            @error="subsidyPdfError = true"
          />
          <pdf
            v-for="page in Math.max(0, subsidyPdfPages - 1)"
            :key="page + 1"
            :src="subsidyPdfUrl"
            :page="page + 1"
            @error="subsidyPdfError = true"
          />
        </template>
      </div>
    </van-popup>

    <van-dialog
      v-model="showRefundConfirm"
      title="退款确认"
      show-cancel-button
      :confirm-button-color="'#b14545'"
      @confirm="startRefund"
    >
      <div class="pay-confirm-body">
        确认为客户「{{ payTargetName }}」办理退款？<br />
        退款后体检权益将作废，该客户不可再预约体检。
      </div>
    </van-dialog>

    <van-overlay :show="paying">
      <van-loading class="pay-loading" size="40" vertical>{{ confirmingPay ? "正在确认支付结果..." : "支付中..." }}</van-loading>
    </van-overlay>

    <van-overlay :show="refunding">
      <van-loading class="pay-loading" size="40" vertical>退款处理中...</van-loading>
    </van-overlay>
  </div>
</template>

<script>
import { Toast } from "vant";
import pdf from "vue-pdf";
import subsidyPdfUrl from "file-loader?name=assets/[name].[hash:8].[ext]!@/assets/sanGao/体检费用补贴说明.pdf";
import { canShowExamReimbursement } from "@/config/sanGao";
import { isTaikangSalesmanMode, isTaikangSalesmanSession } from '@/utils/sanGaoSalesmanMode';
import { isWehat } from "@/router/util";
import {
  getSanGaoSalesmanInfo,
  getSanGaoOrgById,
  getSanGaoCustomerList,
  saveSanGaoCustomerTags,
  paySanGaoCustomer,
  refundSanGaoCustomer,
} from "@/api/sanGao";

const STORAGE_KEYS = {
  token: "san_gao_salesman_token",
  info: "san_gao_salesman_info",
  org: "san_gao_org_info",
  orgId: "san_gao_salesman_orgId",
  mode: "san_gao_salesman_mode",
};

const AUDIT_FILTERS = [
  { text: "全部客户", value: null },
  { text: "待审核", value: 0 },
  { text: "已通过", value: 1 },
  { text: "未通过", value: 2 },
  { text: "已支付", value: 3 },
  { text: "已退款", value: 4 },
];

const EXAM_FILTERS = [
  { text: "体检全部", value: null },
  { text: "未预约", value: 0 },
  { text: "已预约", value: 1 },
  { text: "已取消", value: 2 },
  { text: "已到检", value: 3 },
  { text: "已出报告", value: 4 },
  { text: "已传报告", value: 5 },
];

export default {
  name: "SanGaoSalesmanIndex",
  components: { pdf },
  data() {
    return {
      orgId: "",
      orgInfo: null,
      orgPolicyLoaded: false,
      salesmanName: "",
      salesmanPhone: "",
      salesmanCode: "",
      salesmanId: null,
      activeFilter: null,
      examFilter: null,
      customerList: [],
      loading: false,
      customerDataReady: false,
      customerLoadError: "",
      customerRequestId: 0,
      showTagEditor: false,
      tagTarget: null,
      tagDraft: [],
      tagInput: "",
      tagError: "",
      savingTags: false,
      maxCustomerTags: 8,
      maxCustomerTagLength: 20,
      showExamSummary: false,
      examSummaryPending: true,
      pendingExamScroll: false,
      payTarget: null,
      showPayConfirm: false,
      subsidyPdfUrl,
      showSubsidyPreview: false,
      subsidyPdfPages: 0,
      subsidyPdfLoading: true,
      subsidyPdfError: false,
      paying: false,
      confirmingPay: false,
      payResultTimer: null,
      bridgeReadyHandler: null,
      refundTarget: null,
      showRefundConfirm: false,
      refunding: false,
    };
  },
  computed: {
    taikangMode() { return isTaikangSalesmanMode(this.$route.query) || isTaikangSalesmanSession(); },
    showReimbursementInfo() {
      return this.orgPolicyLoaded && canShowExamReimbursement(this.orgInfo);
    },
    canShowExamSummary() {
      // 支付授权回跳及退款时先完成原流程，避免进度弹窗叠加到收银台。
      return this.examSummaryPending && this.customerDataReady && !this.loading &&
        !this.paying && !this.refunding && !this.showPayConfirm &&
        !this.showRefundConfirm && !this.showSubsidyPreview && !this.showTagEditor;
    },
    payTargetName() {
      const target = this.refundTarget || this.payTarget;
      return (target && (target.nickname || target.phone)) || "匿名用户";
    },
    maskedPhone() {
      if (!this.salesmanPhone || this.salesmanPhone.length < 7) {
        return this.salesmanPhone || "-";
      }
      return `${this.salesmanPhone.slice(0, 3)}****${this.salesmanPhone.slice(-4)}`;
    },
    totalCount() {
      return this.customerList.length;
    },
    // 体检进度筛选只在「全部客户」和「已支付」视图出现（仅已支付客户有体检流程）
    showExamFilter() {
      return this.activeFilter === null || this.activeFilter === 3;
    },
    auditOptions() {
      return AUDIT_FILTERS.map((item) => ({
        text: `${item.text}（${this.countByStatus(item.value)}）`,
        label: item.text,
        count: this.countByStatus(item.value),
        value: item.value,
      }));
    },
    examOptions() {
      return EXAM_FILTERS.map((item) => ({
        text: `${item.text}（${this.countByExamStatus(item.value)}）`,
        label: item.text,
        count: this.countByExamStatus(item.value),
        value: item.value,
      }));
    },
    auditFilterTitle() {
      const option = this.auditOptions.find(item => item.value === this.activeFilter);
      return option ? option.text : '客户状态';
    },
    examFilterTitle() {
      const option = this.examOptions.find(item => item.value === this.examFilter);
      return option ? option.text : '体检进度';
    },
    filteredCustomers() {
      let list = this.customerList;
      if (this.activeFilter !== null) {
        list = list.filter((c) => c.auditStatus === this.activeFilter);
      }
      // 体检进度筛选只在「全部客户」「已支付」视图生效，且只匹配已支付客户
      if (this.showExamFilter && this.examFilter !== null) {
        list = list.filter((c) => c.auditStatus === 3 && this.examStatusSafe(c) === this.examFilter);
      }
      return list;
    },
  },
  watch: {
    canShowExamSummary(ready) {
      if (ready && !this.payPageDisposed) {
        this.examSummaryPending = false;
        this.showExamSummary = true;
      }
    },
    showSubsidyPreview(show) {
      if (show) {
        this.subsidyPdfPages = 0;
        this.subsidyPdfLoading = true;
        this.subsidyPdfError = false;
      }
    },
    activeFilter() {
      // 切换客户状态时重置体检进度筛选，避免残留筛选导致「永远查不到数据」
      this.examFilter = null;
    },
  },
  created() {
    this.payPageDisposed = false;
    this.orgId = this.$route.query.orgId || localStorage.getItem(STORAGE_KEYS.orgId) || "";
    this.loadSalesman();
    this.loadCurrent();
    this.resumePendingPay();
  },
  beforeDestroy() {
    this.payPageDisposed = true;
    this.customerRequestId++;
    clearTimeout(this.payResultTimer);
    document.removeEventListener("WeixinJSBridgeReady", this.bridgeReadyHandler, false);
  },
  methods: {
    customerTags(tags) {
      return Array.isArray(tags) ? tags.filter(tag => typeof tag === "string" && tag.trim()) : [];
    },
    openTagEditor(item) {
      if (!item || this.loading || this.paying || this.refunding || this.savingTags ||
        this.showPayConfirm || this.showRefundConfirm || this.showExamSummary) return;
      this.tagTarget = item;
      this.tagDraft = this.customerTags(item.tags).slice();
      this.tagInput = "";
      this.tagError = "";
      this.showTagEditor = true;
    },
    closeTagEditor() {
      if (this.savingTags) return;
      this.showTagEditor = false;
      this.tagTarget = null;
      this.tagDraft = [];
      this.tagInput = "";
      this.tagError = "";
    },
    addCustomerTag() {
      if (this.savingTags) return false;
      const tag = this.tagInput.trim();
      if (!tag) this.tagError = "请输入标签内容";
      else if (Array.from(tag).length > this.maxCustomerTagLength) this.tagError = "每个标签最多20个字";
      else if (/[\u0000-\u001f\u007f-\u009f]/.test(tag)) this.tagError = "标签不能包含换行或控制字符";
      else if (this.tagDraft.includes(tag)) this.tagError = "该标签已添加";
      else if (this.tagDraft.length >= this.maxCustomerTags) this.tagError = "每位客户最多添加8个标签";
      else {
        this.tagDraft.push(tag);
        this.tagInput = "";
        this.tagError = "";
        return true;
      }
      return false;
    },
    removeCustomerTag(index) {
      if (this.savingTags) return;
      this.tagDraft.splice(index, 1);
      this.tagError = "";
    },
    async saveCustomerTags() {
      if (this.savingTags || !this.tagTarget || !this.showTagEditor) return;
      // 尚未点击“添加”的输入也随保存提交，避免漏掉最后一个标签。
      if (this.tagInput.trim() && !this.addCustomerTag()) return;
      const memberId = this.tagTarget.id;
      this.savingTags = true;
      this.tagError = "";
      try {
        const res = await saveSanGaoCustomerTags({ memberId, tags: this.tagDraft.slice() });
        if (this.payPageDisposed) return;
        if (res && res.code === 200 && Array.isArray(res.data)) {
          const customer = this.customerList.find(item => item.id === memberId);
          if (customer) this.$set(customer, "tags", this.customerTags(res.data));
          this.savingTags = false;
          this.closeTagEditor();
          Toast.success("标签已保存");
        } else {
          this.tagError = (res && res.msg) || "保存失败，请重试";
        }
      } catch (e) {
        if (!this.payPageDisposed) this.tagError = "网络异常，标签未保存，请重试";
      } finally {
        if (!this.payPageDisposed) this.savingTags = false;
      }
    },
    selectCustomerFilter(type, value) {
      if (type === 'audit') this.activeFilter = value;
      else this.examFilter = value;
      const dropdown = this.$refs[type === 'audit' ? 'auditDropdown' : 'examDropdown'];
      if (dropdown) dropdown.toggle(false);
    },
    refreshExamSummary() {
      if (this.loading || this.paying || this.refunding || this.showTagEditor) return;
      this.examSummaryPending = true;
      this.loadCurrent();
    },
    async viewExamCustomers(status) {
      if (status !== 3 && status !== 4) return;
      this.pendingExamScroll = true;
      this.showExamSummary = false;
      this.activeFilter = 3;
      // 等待客户状态的 watcher 清空旧筛选后，再设置目标体检状态。
      await this.$nextTick();
      if (!this.payPageDisposed) this.examFilter = status;
    },
    scrollToExamResults() {
      if (!this.pendingExamScroll || this.payPageDisposed) return;
      this.pendingExamScroll = false;
      const section = this.$refs.customerSection;
      if (section) section.scrollIntoView({ block: "start" });
    },
    // 微信 JSAPI 支付需要付款人（业务员）openid：通过静默授权拿 code。
    // 不在页面加载时跳 OAuth（会导致页面刷新、被主系统 OAuth 接管），
    // 改为用户点击「支付」时按需获取：无 code → 存待支付目标 → OAuth → 回来自动续上。
    triggerWxOAuth() {
      if (!isWehat()) return;
      const appId = "wxb0ea01d4fd25cc75"; // HUA_DA 公众号，与三高修复支付通道一致
      const redirectUri = `https://yisheng.healthmore.net${this.$route.fullPath}`;
      // 授权后回到当前工作台，支付期间保持该页面，不依赖 OAuth 的历史记录返回。
      window.location.href =
        `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&response_type=code&scope=snsapi_base&state=STATE#wechat_redirect`;
    },
    // OAuth 回跳后（router 存了 code），如果有待支付目标，自动续上支付。
    resumePendingPay() {
      const code = localStorage.getItem("san_gao_salesman_code");
      const pending = localStorage.getItem("san_gao_salesman_pending_pay");
      if (!code || !pending) return;
      try {
        const target = JSON.parse(pending);
        this.payTarget = target;
      } catch (e) {
        // ignore
      }
      localStorage.removeItem("san_gao_salesman_pending_pay");
      if (this.payTarget) {
        this.startPay();
      }
    },
    async loadSalesman() {
      const cached = localStorage.getItem(STORAGE_KEYS.info);
      if (cached) {
        try {
          this.fillInfo(JSON.parse(cached));
        } catch (e) {
          // ignore
        }
      }
      const cachedOrg = localStorage.getItem(STORAGE_KEYS.org);
      if (cachedOrg) {
        try {
          const organization = JSON.parse(cachedOrg);
          if (String(organization.orgId || organization.id) === String(this.orgId)) {
            this.orgInfo = organization;
          }
        } catch (e) {
          // ignore
        }
      }
      try {
        const res = await getSanGaoSalesmanInfo();
        if (res && res.code === 200 && res.data) {
          this.fillInfo(res.data);
          localStorage.setItem(STORAGE_KEYS.info, JSON.stringify(res.data));
          this.loadOrgPolicy();
        } else {
          this.goLogin();
        }
      } catch (e) {
        this.goLogin();
      }
    },
    fillInfo(info) {
      this.salesmanId = info.id || null;
      this.salesmanName = info.salesmanName || info.name || "";
      this.salesmanPhone = info.userPhone || "";
      this.salesmanCode = info.salesmanCode || "";
      // 公众号入口登录时无机构参数，以业务员归属机构为准
      if (!this.orgId && info.orgId) {
        this.orgId = String(info.orgId);
        localStorage.setItem(STORAGE_KEYS.orgId, this.orgId);
      }
    },
    async loadOrgPolicy() {
      const orgId = this.orgId;
      this.orgPolicyLoaded = false;
      if (!orgId) return;
      try {
        const res = await getSanGaoOrgById({ orgId });
        if (this.payPageDisposed || String(this.orgId) !== String(orgId)) return;
        if (res && res.code === 200 && res.data && String(res.data.orgId) === String(orgId)) {
          this.orgInfo = res.data;
          this.orgPolicyLoaded = true;
        }
      } catch (e) {
        // 查询失败不阻断支付，也不展示未经确认的报销/补贴说明。
      }
    },
    async loadCurrent() {
      if (this.showTagEditor) return;
      const requestId = ++this.customerRequestId;
      this.loading = true;
      this.customerDataReady = false;
      this.customerLoadError = "";
      this.payTarget = null;
      try {
        const res = await getSanGaoCustomerList({});
        if (this.payPageDisposed || requestId !== this.customerRequestId) return;
        if (res && res.code === 200 && Array.isArray(res.data)) {
          this.customerList = res.data.map(item => ({ ...item, tags: this.customerTags(item.tags) }));
          this.customerDataReady = true;
        } else {
          this.customerList = [];
          this.customerLoadError = (res && res.msg) || "加载客户列表失败";
          Toast(this.customerLoadError);
        }
      } catch (e) {
        if (this.payPageDisposed || requestId !== this.customerRequestId) return;
        this.customerList = [];
        this.customerLoadError = "网络异常，请稍后重试";
        Toast(this.customerLoadError);
      } finally {
        if (!this.payPageDisposed && requestId === this.customerRequestId) this.loading = false;
      }
    },
    countByStatus(value) {
      if (value === null) {
        return this.customerList.length;
      }
      return this.customerList.filter((c) => c.auditStatus === value).length;
    },
    countByExamStatus(value) {
      const pool = this.customerList.filter((c) => c.auditStatus === 3);
      if (value === null) {
        return pool.length;
      }
      return pool.filter((c) => this.examStatusSafe(c) === value).length;
    },
    confirmRefund(item) {
      if (this.refunding) return;
      this.refundTarget = item;
      this.showRefundConfirm = true;
    },
    async startRefund() {
      if (this.refunding || !this.refundTarget) return;
      this.refunding = true;
      try {
        const res = await refundSanGaoCustomer(this.refundTarget.id);
        if (res && res.code === 200) {
          Toast.success("已提交退款，请稍后查看到账");
          this.loadCurrent();
        } else {
          Toast((res && res.msg) || "退款失败");
        }
      } catch (e) {
        Toast("网络异常，请稍后重试");
      } finally {
        this.refunding = false;
        this.refundTarget = null;
      }
    },
    goExam(item) {
      this.$router.push({
        path: "/sanGao/salesman/exam",
        query: { memberId: item.id },
      });
    },
    examStatusSafe(item) {
      const status = item.examStatus;
      return status === null || status === undefined ? 0 : status;
    },
    examText(item) {
      const status = this.examStatusSafe(item);
      if (status === 0) return "未预约";
      if (status === 1) return "已预约";
      if (status === 2) return "已取消";
      if (status === 3) return "已到检";
      if (status === 4) return "已出报告";
      if (status === 5) return "已传报告";
      return "-";
    },
    formatDate(value) {
      if (!value || value.length !== 8) return value || "-";
      return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
    },
    confirmPay(item) {
      if (this.paying) return;
      this.payTarget = item;
      this.showPayConfirm = true;
    },
    async startPay() {
      if (this.paying || !this.payTarget) return;
      // 无 wxCode 时先去 OAuth 拿 code，回来后 resumePendingPay 自动续上
      const wxCode = localStorage.getItem("san_gao_salesman_code");
      if (!wxCode) {
        if (!isWehat()) {
          Toast("请在微信中打开页面进行支付");
          return;
        }
        localStorage.setItem(
          "san_gao_salesman_pending_pay",
          JSON.stringify(this.payTarget)
        );
        this.showPayConfirm = false;
        this.triggerWxOAuth();
        return;
      }
      this.paying = true;
      this.showPayConfirm = false;
      const customerId = this.payTarget.id;
      try {
        const res = await paySanGaoCustomer({
          customerIds: [customerId],
          wxCode,
        });
        localStorage.removeItem("san_gao_salesman_code");
        if (this.payPageDisposed) return;
        if (res && res.code === 200) {
          // 原页面直接调起收银台，返回后由支付回调刷新列表，避免提前销毁支付发起页。
          this.invokeWechatPay(res.data, customerId);
        } else {
          this.paying = false;
          // code 可能已被消费/失效，清掉以便下次重新授权
          localStorage.removeItem("san_gao_salesman_code");
          Toast((res && res.msg) || "发起支付失败");
        }
      } catch (e) {
        this.paying = false;
        localStorage.removeItem("san_gao_salesman_code");
        Toast("网络异常，请稍后重试");
      }
    },
    invokeWechatPay(payParams, customerId) {
      if (!payParams) {
        this.paying = false;
        Toast("未获取到支付信息");
        return;
      }
      const invoke = () => {
        document.removeEventListener("WeixinJSBridgeReady", invoke, false);
        this.bridgeReadyHandler = null;
        if (this.payPageDisposed) return;
        window.WeixinJSBridge.invoke(
          "getBrandWCPayRequest",
          {
            appId: payParams.appId,
            timeStamp: payParams.timeStamp,
            nonceStr: payParams.nonceStr,
            package: payParams.packages,
            signType: payParams.signType,
            paySign: payParams.paySign,
          },
          result => {
            if (this.payPageDisposed) return;
            if (result && result.err_msg === "get_brand_wcpay_request:ok") {
              this.confirmingPay = true;
              this.refreshPayResult(customerId);
            } else {
              this.paying = false;
              if (!result || result.err_msg !== "get_brand_wcpay_request:cancel") {
                Toast("支付未完成，请重试");
              }
            }
          }
        );
      };
      if (typeof window.WeixinJSBridge === "undefined") {
        this.bridgeReadyHandler = invoke;
        document.addEventListener("WeixinJSBridgeReady", invoke, false);
      } else {
        invoke();
      }
    },
    async refreshPayResult(customerId, attempt = 0) {
      // 微信回调不代表后台通知已处理完；以服务端客户状态为准，最多确认 6 次。
      try {
        const res = await getSanGaoCustomerList({});
        if (this.payPageDisposed) return;
        if (res && res.code === 200 && Array.isArray(res.data)) {
          this.customerList = res.data.map(item => ({ ...item, tags: this.customerTags(item.tags) }));
          this.customerDataReady = true;
          this.customerLoadError = "";
          const target = res.data.find(item => String(item.id) === String(customerId));
          if (target && target.auditStatus === 3) {
            this.paying = false;
            this.confirmingPay = false;
            this.payTarget = null;
            Toast.success("支付成功");
            return;
          }
        }
      } catch (e) {
        // 短暂网络异常时继续确认，不能据此判定支付失败。
      }
      if (this.payPageDisposed) return;
      if (attempt < 5) {
        this.payResultTimer = setTimeout(() => this.refreshPayResult(customerId, attempt + 1), 3000);
      } else {
        this.paying = false;
        this.confirmingPay = false;
        Toast("支付结果同步中，请稍后刷新列表确认，请勿重复支付");
      }
    },
    auditText(status) {
      if (status === 0) return "待审核";
      if (status === 1) return "已通过";
      if (status === 2) return "未通过";
      if (status === 3) return "已支付";
      if (status === 4) return "已退款";
      return "-";
    },
    maskIdCard(idCard) {
      if (!idCard) return "-";
      if (idCard.includes("*")) return idCard;
      if (idCard.length === 18) return `${idCard.slice(0, 6)}********${idCard.slice(-4)}`;
      if (idCard.length === 15) return `${idCard.slice(0, 4)}********${idCard.slice(-3)}`;
      return "****";
    },
    formatTime(value) {
      if (!value) return "-";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "-";
      const pad = (n) => (n < 10 ? `0${n}` : String(n));
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    goQrcode() {
      if (!this.salesmanId) {
        Toast("业务员信息加载中，请稍候");
        return;
      }
      this.$router.push({
        path: "/sanGao/salesman/qrcode",
        query: { orgId: this.orgId, salesmanId: this.salesmanId },
      });
    },
    goOfficialAccount() {
      if (this.taikangMode) return;
      this.$router.push({ path: "/sanGao/salesman/officialAccount" });
    },
    goLogin(taikangMode = this.taikangMode) {
      // 退出登录/未登录跳转统一进登录页签
      const query = { type: "login" };
      if (taikangMode) query.taikang = '1';
      if (this.orgId) {
        query.orgId = this.orgId;
      }
      this.$router.replace({ path: "/sanGao/salesman/login", query });
    },
    logout() {
      const taikangMode = this.taikangMode;
      localStorage.removeItem(STORAGE_KEYS.token);
      localStorage.removeItem(STORAGE_KEYS.info);
      localStorage.removeItem(STORAGE_KEYS.mode);
      localStorage.removeItem('san_gao_salesman_code');
      localStorage.removeItem('san_gao_salesman_pending_pay');
      Toast("已退出登录");
      this.goLogin(taikangMode);
    },
  },
};
</script>

<style lang="less" scoped>
@customer-card-inset: 16px;
@customer-filter-inset: 14px;
@customer-dropdown-inset: (@customer-card-inset + @customer-filter-inset);

.salesman-home {
  position: relative;
  min-height: 100vh;
  padding: 0 0 32px;
  background: linear-gradient(180deg, #0874ca 0%, #4ba8e0 22%, #dff6ff 48%, #f4fafd 100%);
  color: #163b5c;
}

.home-header {
  padding: 22px 18px 16px;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 48, 113, 0.35);
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-text {
  flex: 1;
  min-width: 0;
}

.home-label {
  margin: 0 0 6px;
  font-size: 11px;
  letter-spacing: 2px;
  opacity: 0.9;
}

.home-header h1 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
}

.home-org {
  margin: 4px 0 0;
  font-size: 12px;
  opacity: 0.92;
}

.header-meta {
  margin-top: 8px;
  font-size: 12px;
  opacity: 0.88;
}

.header-meta .dot {
  margin: 0 8px;
  opacity: 0.6;
}

.logout-button-sm {
  height: 28px;
  padding: 0 12px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 14px;
  font-size: 12px;
  color: #fff;
  background: transparent;
  cursor: pointer;
}

.link-card {
  display: flex;
  align-items: center;
  margin: 0 16px 14px;
  padding: 14px 14px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(12, 91, 147, 0.1);
  cursor: pointer;
}

.link-icon {
  display: flex;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin-right: 12px;
  border-radius: 50%;
  color: #fff;
  background: linear-gradient(135deg, #19a2e5, #146bd2);
}

.link-icon .van-icon {
  font-size: 20px;
}

.link-icon.oa {
  background: linear-gradient(135deg, #2bb35a, #17974a);
}

.link-text {
  flex: 1;
  min-width: 0;
}

.link-text strong {
  display: block;
  font-size: 15px;
  color: #163b5c;
}

.link-text small {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #7c8d9c;
}

.customer-section {
  margin: 0 @customer-card-inset;
  padding: 0 0 12px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(12, 91, 147, 0.08);
}

.section-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 14px 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #163b5c;
}

.section-title small {
  font-size: 11px;
  font-weight: 400;
  color: #7c8d9c;
}

.customer-title-actions {
  display: flex;
  align-items: center;
  gap: 10px;

  button {
    min-height: 44px;
    padding: 0 4px;
    border: 0;
    background: transparent;
    color: #1670c0;
    font-size: 12px;
    cursor: pointer;
  }

  button:disabled { opacity: 0.5; }
}

.exam-summary-body {
  padding: 0 20px 18px;
  text-align: center;
}

.exam-summary-intro {
  margin: 8px 0 16px;
  color: #607a8e;
  font-size: 13px;
}

.exam-summary-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.exam-summary-stats .exam-summary-stat:last-child { grid-column: 1 / -1; }

.exam-summary-stat {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  padding: 16px 6px 12px;
  border: 1px solid #dceefa;
  border-radius: 12px;
  background: #f0f8ff;
  color: #1670c0;
  font-size: 14px;
  cursor: pointer;

  strong { margin: 8px 0 4px; font-size: 30px; line-height: 1.2; }
  small { margin-left: 4px; font-size: 12px; font-weight: 400; }
  em { font-size: 11px; font-style: normal; color: #607a8e; }
  .exam-summary-link { margin-top: 12px; font-size: 12px; }
  &:focus-visible { outline: 2px solid #1670c0; outline-offset: 2px; }
  &:active { opacity: 0.75; }
  &.reported { border-color: #d7efe4; background: #f0faf5; color: #1b8a4b; }
}

.exam-summary-note {
  margin: 14px 0 0;
  font-size: 11px;
  line-height: 1.7;
  text-align: left;
  color: #7c8d9c;
}

.filter-dropdown {
  margin: 4px @customer-filter-inset 8px;

  ::v-deep .van-dropdown-menu__bar {
    height: 44px;
    background: transparent;
    box-shadow: none;
  }

  ::v-deep .van-dropdown-menu__item {
    border: 1px solid #e3ecf3;
    border-radius: 10px;
    background: #f5f8fb;
    & + .van-dropdown-menu__item { margin-left: 10px; }
  }

  ::v-deep .van-dropdown-menu__title {
    padding: 0 14px 0 2px;
    font-size: 13px;
    font-weight: 500;
    color: #4c6579;
    &::after { right: 0; }
    &--active { color: #1677c8; }
  }

  ::v-deep .van-overlay { background: rgba(25, 49, 70, 0.22); }
}

// Vant 下拉容器固定在视口上，仅收窄选项面板，保留完整遮罩和点击外部关闭。
.filter-dropdown ::v-deep .van-dropdown-item__content {
  top: 8px;
  left: @customer-dropdown-inset;
  right: @customer-dropdown-inset;
  width: auto;
  max-height: calc(100% - 24px);
  box-sizing: border-box;
  padding: 8px;
  background: #fff;
  border: 1px solid #e3edf5;
  border-radius: 14px;
  box-shadow: 0 10px 28px rgba(24, 63, 94, 0.16);
}

.filter-panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #476278;
  span { font-size: 10px; font-weight: 400; color: #91a2b1; }
}

.filter-option {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  margin: 2px 0;
  padding: 10px 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #405a6e;
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  .filter-option-label { flex: 1; }
  .filter-option-count {
    min-width: 24px;
    padding: 2px 6px;
    border-radius: 6px;
    background: #f1f5f8;
    color: #7c909f;
    font-size: 11px;
    line-height: 18px;
    text-align: center;
  }
  .filter-option-check { margin-left: 12px; color: #cbd8e2; font-size: 17px; }
  &.is-selected {
    background: #eaf4ff;
    color: #1677c8;
    font-weight: 600;
    .filter-option-count { background: #d8ebfc; color: #1677c8; }
    .filter-option-check { color: #1677c8; }
  }
  &:active { background: #e3eff9; }
  &:focus-visible { outline: 2px solid #1677c8; outline-offset: -2px; }
}

.customer-list {
  padding: 8px 14px 6px;
}

.state-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: #7c8d9c;
}

.state-cell.empty p {
  margin: 8px 0 0;
  font-size: 12px;
}

.customer-card {
  padding: 12px 12px 10px;
  margin-bottom: 10px;
  border: 1px solid #eef4f9;
  border-radius: 10px;
  background: #fafdff;
}

.customer-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.customer-name {
  font-size: 14px;
  font-weight: 600;
  color: #163b5c;
}

.customer-tags-row {
  display: flex;
  align-items: flex-start;
  margin: 2px 0 8px;
}

.customer-tags, .tag-draft-list {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  min-width: 0;
  margin-bottom: -6px;
}

.customer-tags { margin-right: 8px; }

.customer-tag {
  display: inline-block;
  max-width: 100%;
  margin: 0 6px 6px 0;
  padding: 4px 8px;
  border-radius: 5px;
  background: #e8f3ff;
  color: #1677c8;
  font-size: 12px;
  line-height: 18px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.edit-tags-button {
  flex-shrink: 0;
  min-height: 30px;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: #1677c8;
  font-size: 12px;
  cursor: pointer;
}

.tag-editor {
  display: flex;
  flex-direction: column;
  max-height: 85%;
  color: #163b5c;
}

.tag-editor-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 20px 12px;
  h2 { margin: 0; font-size: 18px; }
  p { margin: 6px 0 0; color: #5b7488; font-size: 14px; }
}

.tag-editor-close {
  padding: 8px;
  border: 0;
  background: transparent;
  color: #7c909f;
  font-size: 18px;
}

.tag-editor-body {
  min-height: 0;
  overflow-y: auto;
  padding: 0 20px 20px;
  -webkit-overflow-scrolling: touch;
}

.tag-editor-hint, .tag-empty {
  margin: 0 0 14px;
  font-size: 12px;
  color: #7c909f;
  line-height: 20px;
}

.tag-input-row {
  display: flex;
  align-items: center;
  padding-right: 10px;
  border: 1px solid #dbe8f4;
  border-radius: 8px;
  background: #f5f9fe;
  .tag-input { flex: 1; min-width: 0; background: transparent; }
  /deep/ .van-field__label { display: none; }
}

.tag-add-button {
  flex-shrink: 0;
  padding: 6px 12px;
  border: 0;
  border-radius: 6px;
  background: #1677c8;
  color: #fff;
  font-size: 13px;
}

.tag-editor-error {
  margin: 8px 0 0;
  color: #b14545;
  font-size: 12px;
  line-height: 18px;
}

.tag-draft-heading {
  display: flex;
  justify-content: space-between;
  margin: 20px 0 10px;
  font-size: 13px;
  span { color: #7c909f; }
}

.tag-draft-item {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  > span { min-width: 0; }
  button {
    flex-shrink: 0;
    min-width: 28px;
    min-height: 28px;
    margin: -4px -4px -4px 4px;
    padding: 4px;
    border: 0;
    background: transparent;
    color: #1677c8;
  }
}

.tag-editor-actions {
  display: flex;
  padding: 12px 20px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  border-top: 1px solid #eef4f9;
  .van-button { flex: 1; border-radius: 8px; }
  .van-button:first-child { margin-right: 12px; }
}

.edit-tags-button:disabled, .tag-editor button:disabled { opacity: .5; }

.audit-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
}

.audit-0 {
  color: #b6820b;
  background: #fff5d6;
}

.audit-1 {
  color: #1b8a4b;
  background: #dff7e8;
}

.audit-2 {
  color: #b14545;
  background: #fde0e0;
}

.audit-3 {
  color: #1670c0;
  background: #ddf0fd;
}

.audit-4 {
  color: #8a63c9;
  background: #efe6fc;
}

.customer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 11px;
  color: #93a8b8;
}

.pay-single-button {
  height: 30px;
  min-width: 72px;
  padding: 0 14px;
  border: 0;
  border-radius: 15px;
  font-size: 12px;
  color: #fff;
  background: linear-gradient(100deg, #19a2e5, #146bd2);
  box-shadow: 0 4px 10px rgba(17, 112, 199, 0.25);
}

.pay-single-button:disabled {
  opacity: 0.6;
}

.footer-buttons {
  display: inline-flex;
  gap: 8px;
}

.pay-single-button.ghost {
  color: #1670c0;
  background: #ddf0fd;
  box-shadow: none;
}

.pay-single-button.refund {
  color: #b14545;
  background: #fde0e0;
  box-shadow: none;
}

.exam-inline {
  font-weight: 600;
}

.exam-0 {
  color: #7c8d9c;
}

.exam-1 {
  color: #1670c0;
}

.exam-2 {
  color: #b6820b;
}

.exam-3 {
  color: #0f9c8f;
}

.exam-4,
.exam-5 {
  color: #1b8a4b;
}

.pay-confirm-body {
  padding: 20px 24px 26px;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  color: #4a6a82;
}

.pay-confirm-title {
  display: inline-flex;
  align-items: baseline;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
}

.pay-confirm-message {
  font-size: 16px;
  line-height: 1.8;
  overflow-wrap: break-word;
}

.pay-confirm-name {
  font-size: 18px;
  font-weight: 700;
  color: #163b5c;
}

.pay-confirm-action {
  display: inline-block;
}

.pay-subsidy-link {
  margin-left: 16px;
  padding: 4px 2px;
  font-size: 12px;
  font-weight: 400;
  color: #808a93;
  text-decoration: underline;
}

.subsidy-preview {
  display: flex;
  flex-direction: column;
  height: 85%;
  border-radius: 14px 14px 0 0;
  background: #f4f7fa;
}

.subsidy-preview-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 8px 16px;
  background: #fff;
  font-size: 14px;
  button {
    min-width: 48px;
    min-height: 40px;
    border: 0;
    background: transparent;
    color: #4a6a82;
    font-size: 14px;
  }
}

.subsidy-preview-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  .van-loading,
  .subsidy-preview-error {
    padding: 24px 0;
    text-align: center;
    font-size: 13px;
  }
}

.pay-loading {
  position: absolute;
  top: 42%;
  left: 50%;
  color: #fff;
  transform: translate(-50%, -50%);
}

.customer-meta {
  display: flex;
  font-size: 12px;
  color: #5b7488;
  margin-top: 4px;
}
</style>
